import React, { useState } from 'react';
import { Box, Button, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import DialogWithEnterAction from '../../components/DialogWithEnterAction';
import { ActionButton } from '../../components/ActionButton';
import { useControllerConnectionStatus } from '../../Hooks/useControllerConnectionStatus';
import Desc from '../../FormWidgets/Description';
import RootSenseFirmwareLinks from '../../FormWidgets/RootSenseFirmwareLinks';
import { getRootZoneAddressUnavailableReason, supportsRootZoneAddressSetup } from '../../Utils/irrigation_utils';
import { HandleAction } from '../../Utils/utils';

// RootSense Link assigns SDI-12 addresses to irrigation groups, case sensitively.
const ADDRESS_GROUPS = [
  { range: '0–9, a–m', addresses: '0123456789abcdefghijklm' },
  { range: 'n–z', addresses: 'nopqrstuvwxyz' },
  { range: 'A–M', addresses: 'ABCDEFGHIJKLM' },
  { range: 'N–Z', addresses: 'NOPQRSTUVWXYZ' }
];

export default function RootZoneAddressSetup({ controller, wsManager }) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState('0');
  const liveController = useSnapshot(controller);
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);
  const canRead = Boolean(liveController.access_rights?.nutrition?.read);
  const canWrite = Boolean(liveController.access_rights?.nutrition?.write);
  const unavailableReason = getRootZoneAddressUnavailableReason(liveController);

  const sendAction = (action, value, permission) => {
    if (controller.connection?.status !== 'good') return;
    if (!supportsRootZoneAddressSetup(controller) || !controller.access_rights?.nutrition?.[permission] ||
        getRootZoneAddressUnavailableReason(controller))
      return;
    let sent = false;
    try {
      sent = HandleAction(wsManager, controller, action, value);
    } catch (_) {
      // Retain the selection for an explicit retry; never replay on reconnect.
    }
    enqueueSnackbar(t(sent ? 'root_zone_address_request_sent' : 'root_zone_address_send_failed'), {
      variant: sent ? 'info' : 'error'
    });
    // The existing controller message path reports physical completion or failure.
  };
  const changeAddress = () => sendAction('action_root_zone_change_address', address.charCodeAt(0), 'write');
  const readAddress = () => sendAction('action_root_zone_read_address', 0, 'read');

  if (!supportsRootZoneAddressSetup(liveController)) return null;

  return (
    <Box mb={2}>
      <ActionButton variant="outlined" color="primary" title={connectionDisabledReason}
        disabled={connectionStatus !== 'good' || (!canRead && !canWrite)} onClick={() => setOpen(true)}>
        {t('root_zone_address_setup')}
      </ActionButton>
      <DialogWithEnterAction
        open={open}
        onClose={() => setOpen(false)}
        onEnterAction={changeAddress}
        enterActionDisabled={connectionStatus !== 'good' || !canWrite || Boolean(unavailableReason)}
        aria-labelledby="root-zone-address-title"
      >
        <DialogTitle id="root-zone-address-title">
          <Desc id="root_zone_address_setup" />
        </DialogTitle>
        <DialogContent>
          <Typography paragraph>{t('root_zone_address_single_sensor')}</Typography>
          {unavailableReason && <Typography paragraph role="status">{t(unavailableReason)}</Typography>}
          {unavailableReason === 'gc_ms_root_addr_update_required' && <RootSenseFirmwareLinks />}
          <ActionButton onClick={readAddress} color="primary" title={connectionDisabledReason}
            disabled={connectionStatus !== 'good' || !canRead || Boolean(unavailableReason)}>
            {t('action_root_zone_read_address')}
          </ActionButton>
          <Typography>{t('root_zone_address_assignments')}</Typography>
          {ADDRESS_GROUPS.map((group, index) => (
            <Typography key={group.range}>
              {`${t('irrigation')} ${index + 1}: ${group.range}`}
            </Typography>
          ))}
          <TextField
            id="root-zone-new-address"
            label={t('root_zone_address_new')}
            select
            SelectProps={{ native: true }}
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            disabled={!canWrite}
            fullWidth
            margin="normal"
          >
            {ADDRESS_GROUPS.map((group, index) => (
              <optgroup key={group.range} label={`${t('irrigation')} ${index + 1}`}>
                {[...group.addresses].map((value) => <option key={value} value={value}>{value}</option>)}
              </optgroup>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">{t('close')}</Button>
          <ActionButton onClick={changeAddress} color="primary" title={connectionDisabledReason}
            disabled={connectionStatus !== 'good' || !canWrite || Boolean(unavailableReason)}>
            {t('action_root_zone_change_address')}
          </ActionButton>
        </DialogActions>
      </DialogWithEnterAction>
    </Box>
  );
}
